import React from "react";

import { useTurnstile, Turnstile } from "react-turnstile";

import { type VerificationService } from "@/lib/types/types";

import { getQueryOrOption } from "@/lib/ServerApi/TestingRouteOverrides";

import { QUERY_STRING_CAPTCHA_ERROR_OVERRIDE } from "@/constants";

import { setViewModel } from "@/lib/VerificationService/ViewModel";
import { updateFieldValidationErrorsByFieldId } from "@/lib/utils/stepComponentHelpers/stepComponentHelpers";

import { isCaptchaEnabled } from "@/lib/ProgramTheme/programThemeGetters";

import { CaptchaNotificationComponent, type CaptchaUIState } from "./CaptchaNotificationComponent";
import { useCaptchaMetrics } from "./useCaptchaMetrics";

export interface CaptchaComponentProps {
  verificationService: VerificationService;
  isErrored: boolean;
}

const CAPTCHA_LOAD_TIMEOUT = 10000; // 10 seconds

/**
 * CloudFlare Turnstile invisible captcha. Conditionally loads the captcha based on program theme and
 * renders the captcha notification banner (loading, success, or error).
 */
export const CaptchaComponent = ({ verificationService, isErrored }: CaptchaComponentProps) => {
  const turnstile = useTurnstile();

  const { programTheme, programId } = verificationService;
  const segment = verificationService.verificationResponse.segment;
  const captchaEnabled = isCaptchaEnabled(programTheme);
  const {
    markRendered,
    markVerified,
    incrementRetryCount,
    markServerError,
    recordClientError,
    markScriptLoadError,
    markBrowserUnsupported,
    markLoadTimeout,
    emit: emitCaptchaMetrics,
  } = useCaptchaMetrics({ segment, programId });

  // Any value other than loading/success/hidden falls through to the error banner.
  const mockOverride = getQueryOrOption(QUERY_STRING_CAPTCHA_ERROR_OVERRIDE);
  const isMocking = !!mockOverride;
  const mockUIState: CaptchaUIState | null =
    mockOverride === "loading" || mockOverride === "success"
      ? mockOverride
      : mockOverride === "hidden"
        ? "idle"
        : null;
  const mockHasError = !!mockOverride && mockUIState === null;

  const [uiState, setUIState] = React.useState<CaptchaUIState>("idle");
  const [hasLoaded, setHasLoaded] = React.useState(false);

  // Local source of truth so Turnstile callbacks flip the banner immediately, without waiting on the
  // viewModel/fieldValidationErrors round-trip. Synced with isErrored prop for server rejections.
  const [captchaError, setCaptchaError] = React.useState(isErrored);
  React.useEffect(() => {
    setCaptchaError(isErrored);
  }, [isErrored]);

  const handleRetry = () => {
    incrementRetryCount();
    setUIState("loading");
    turnstile?.reset();
  };

  const updateCaptchaToken = (value: string) => {
    setViewModel({ captchaToken: value });
    updateFieldValidationErrorsByFieldId("captchaToken", value, verificationService);
  };

  const registerCaptchaField = () => {
    const fieldsToSkip = verificationService.viewModel?.fieldsToSkipValidation || [];
    setViewModel({
      fieldsToSkipValidation: fieldsToSkip.filter((id) => id !== "captchaToken"),
    });
    updateCaptchaToken("");
  };

  const unregisterCaptchaField = () => {
    const fieldsToSkip = verificationService.viewModel?.fieldsToSkipValidation || [];
    setViewModel({
      captchaToken: undefined,
      fieldsToSkipValidation: fieldsToSkip.includes("captchaToken")
        ? fieldsToSkip
        : [...fieldsToSkip, "captchaToken"],
    });
  };

  const handleTurnstileVerify = (token: string) => {
    markVerified();
    // Show success only when transitioning out of a visible loading state (stays hidden on background verifies).
    setUIState((prev) => (prev === "loading" ? "success" : prev));
    setCaptchaError(false);
    updateCaptchaToken(token);
  };

  const handleTurnstileError = (errorCode: string) => {
    if (!hasLoaded) {
      markScriptLoadError();
    } else {
      const code = parseInt(errorCode, 10);
      recordClientError(isNaN(code) ? 0 : code);
    }
    setUIState("idle");
    setCaptchaError(true);
    updateCaptchaToken("");
  };

  const handleTurnstileUnsupported = () => {
    markBrowserUnsupported();
    setUIState("idle");
    setCaptchaError(true);
    updateCaptchaToken("");
  };

  // Force a new challenge when captchaToken validation is errored (server rejection or
  // empty token submission). Skip when the script never loaded, reset would hang in "loading".
  React.useEffect(() => {
    if (!captchaEnabled || !isErrored || !hasLoaded) {
      return;
    }

    markServerError();
    setUIState("loading");
    updateCaptchaToken("");
    turnstile?.reset();
  }, [captchaEnabled, isErrored, hasLoaded]);

  // Register captchaToken for form validation, and report the accumulated captcha metrics when the
  // captcha unmounts (collect complete) or the page closes.
  React.useEffect(() => {
    if (!captchaEnabled) {
      return;
    }
    // Wait for parent step effects to finish first. They can overwrite the whole viewModel
    // and would clear the captchaToken we just registered (ex: student's CVEC effect).
    const registerTimeout = setTimeout(registerCaptchaField, 0);

    window.addEventListener("beforeunload", emitCaptchaMetrics);

    return () => {
      clearTimeout(registerTimeout);
      window.removeEventListener("beforeunload", emitCaptchaMetrics);
      emitCaptchaMetrics();
      unregisterCaptchaField();
    };
  }, [captchaEnabled]);

  // Show the error banner if Turnstile silently fails to load within 10s (script blocked, slow network).
  React.useEffect(() => {
    if (!captchaEnabled || hasLoaded || isMocking) {
      return;
    }
    const timer = setTimeout(() => {
      markLoadTimeout();
      // Local state only, updateCaptchaToken would clear field errors set by submit.
      setCaptchaError(true);
    }, CAPTCHA_LOAD_TIMEOUT);

    return () => clearTimeout(timer);
  }, [captchaEnabled, hasLoaded]);

  if (!captchaEnabled) {
    return null;
  }

  return (
    <div>
      {!isMocking && (
        <Turnstile
          sitekey={import.meta.env.VITE_CAPTCHA_SITE_KEY}
          refreshExpired="auto"
          onLoad={() => {
            setHasLoaded(true);
            markRendered();
          }}
          onVerify={handleTurnstileVerify}
          onExpire={() => {
            updateCaptchaToken("");
          }}
          onError={handleTurnstileError}
          onUnsupported={handleTurnstileUnsupported}
        />
      )}

      <CaptchaNotificationComponent
        hasError={captchaError || mockHasError}
        uiState={mockUIState ?? uiState}
        onRetry={hasLoaded && uiState === "idle" && captchaError ? handleRetry : undefined}
      />
    </div>
  );
};
